import {CommandRequester} from '../../../CommandRequester';
import {DisplayViewType} from '@jibo/command-protocol';
import {Display as DisplayRequester, DisplayToken} from './requesters/Display';
import {Gesture, ScreenGestureToken} from './requesters/Gesture';

/**
 * Commands for working with Jibo's screen
 * @namespace CommandRequester.display
 */
export class Display {
    
    private displayRequest: DisplayRequester;

    /**
     * Commands for subscribing to screen-related events
     * @namespace CommandRequester.display.subscribe
     */
    public subscribe: Subscriptions;

    constructor(requester:CommandRequester) {
        this.displayRequest = new DisplayRequester(requester);
        this.subscribe = new Subscriptions(requester);
    }

    /**
     * Create a view that displays Jibo's eye on screen
     * @method CommandRequester.display#EyeView
     * @param {string} name Unique name for the EyeView
     */
    public EyeView(name: string):JIBO.v1.EyeView {
        return {
            Type: DisplayViewType.Eye,
            Name: name
        } as JIBO.v1.EyeView;
    }

    /**
     * Create a view to display an image on Jibo's screen
     * @method CommandRequester.display#ImageView
     * @param {string} name Unique name for the ImageView
     * @param {CommandRequester.display.ImageData} data ImageData for the image to be created in the view
     */
    public ImageView(name: string, data: CommandRequester.display.ImageData):JIBO.v1.ImageView {
        return {
            Type: DisplayViewType.Image,
            Name: name,
            Image: data
        } as JIBO.v1.ImageView;
    }

    /**
     * Create a view to display text on Jibo's screen
     * @method CommandRequester.display#TextView
     * @param {string} name Unique name for the TextView
     * @param {string} text Text to be displayed
     */
    public TextView(name: string, text: string): JIBO.v1.TextView {
        return {
            Type: DisplayViewType.Text,
            Name: name,
            Text: text
        } as JIBO.v1.TextView;
    }

    /**
     * Create an empty view on Jibo's screen
     * @method CommandRequester.display#EmptyView
     * @param  {string} name Unique name of view.
     */
    public EmptyView(name:string):JIBO.v1.EmptyView {
        return {
            Type: DisplayViewType.Empty,
            Name: name,
        } as JIBO.v1.EmptyView;
    }

    /**
     * Replace the existing view with the one given.
     * @method CommandRequester.display#swap
     * @param  {CommandRequester.display#EyeView | CommandRequester.display#TextView | CommandRequester.display#ImageView} view 
     * View to replace the existing one with.
     * @return {DisplayToken}
     */
    public swap(
        view: JIBO.v1.EyeView | JIBO.v1.TextView | JIBO.v1.ImageView,
        openTransition?: JIBO.v1.DisplayTransitions.DisplayTransitionType,
        closeTransition?: JIBO.v1.DisplayTransitions.DisplayTransitionType
    ): DisplayToken {
        const protocol = DisplayRequester.generateProtocol(view, openTransition, closeTransition);
        return this.displayRequest.generateToken(protocol, true);
    }
}

export class Subscriptions {

    private gestureRequest:Gesture;

    constructor(requester: CommandRequester) {
        this.gestureRequest = new Gesture(requester);
    }
    
    /**
     * Listen for screen touch input
     * @method CommandRequester.display.subscribe#gesture
     * @param  {CommandRequester.display.ScreenGestureFilter} [filter={}] Data for screen touch info
     * @return {ScreenGestureToken}
     */
    gesture(filter: CommandRequester.display.ScreenGestureFilter = {}): ScreenGestureToken {
        const filterType = filter
            ? CommandRequester.display.ScreenGestureType[filter.type]
            : undefined;
        const protocol = Gesture.generateProtocol({
            Area: filter.area,
            Type: filterType,
        });
        return this.gestureRequest.generateToken(protocol, true);
    }
}